import {
  isClientCreditNoteDocument,
  isSupplierCreditNoteDocument,
  canonicalArchiveDocTypeKey,
} from '../../../shared/utils/documentTypeUtils';

function expandTypeKeys(keys) {
  return keys.flatMap((k) => {
    const s = String(k);
    return [s, s.replace(/-/g, '_'), s.replace(/_/g, '-')];
  });
}

function buildTypeSet(keys) {
  return new Set(expandTypeKeys(keys));
}

function matchesAllowedTypes(doc, allowedTypeKeys) {
  const typeSet = buildTypeSet(allowedTypeKeys);
  const raw = String(doc?.type ?? '').trim();
  if (!raw) return false;
  if (typeSet.has(raw)) return true;
  if (typeSet.has(raw.replace(/-/g, '_'))) return true;
  if (typeSet.has(raw.replace(/_/g, '-'))) return true;
  const canonical = raw.replace(/-/g, '_').toLowerCase();
  return typeSet.has(canonical);
}

/** @typedef {{ route: string, titleKey: string, descriptionKey: string, allowedTypeKeys: string[], documentFilter?: (doc: object) => boolean, createActions: Array<{ type: string, labelKey: string, party?: 'client'|'supplier' }> }} FacturationSectionArchiveConfig */

/** Saudi billing — each section = saved documents list + create button (SITERP pattern). */
export const FACTURATION_SECTION_ARCHIVES = {
  'demande-prix': {
    route: '/facturation/demande-prix',
    titleKey: 'modules.facturation.subsections.quote_proforma_requests',
    descriptionKey: 'facturation_sections.demande_prix.description',
    allowedTypeKeys: ['quote-request', 'quote_request'],
    createActions: [
      { type: 'quote-request', labelKey: 'facturation_sections.create.quote_request' },
    ],
  },
  'devis-proformas': {
    route: '/facturation/devis-proformas',
    titleKey: 'modules.facturation.subsections.quotes_proformas',
    descriptionKey: 'facturation_sections.devis_proformas.description',
    allowedTypeKeys: ['quote'],
    createActions: [
      { type: 'quote', labelKey: 'facturation_sections.create.quote' },
    ],
  },
  'bons-commande': {
    route: '/facturation/bons-commande',
    titleKey: 'modules.facturation.subsections.purchase_orders',
    descriptionKey: 'facturation_sections.bons_commande.description',
    allowedTypeKeys: ['purchase-order', 'purchase_order'],
    createActions: [
      { type: 'purchase-order', labelKey: 'facturation_sections.create.purchase_order' },
    ],
  },
  receptions: {
    route: '/facturation/receptions',
    titleKey: 'modules.facturation.subsections.receptions',
    descriptionKey: 'facturation_sections.receptions.description',
    allowedTypeKeys: ['reception-note', 'reception_note', 'receipt-note', 'receipt_note'],
    createActions: [
      { type: 'reception-note', labelKey: 'facturation_sections.create.reception_note' },
    ],
  },
  factures: {
    route: '/facturation/factures',
    titleKey: 'modules.facturation.subsections.invoices',
    descriptionKey: 'facturation_sections.factures.description',
    allowedTypeKeys: ['invoice', 'credit-note', 'credit_note'],
    createActions: [
      { type: 'invoice', labelKey: 'facturation_sections.create.invoice' },
      { type: 'credit-note', labelKey: 'facturation_sections.create.credit_note_client' },
    ],
  },
  'bons-livraison': {
    route: '/facturation/bons-livraison',
    titleKey: 'modules.facturation.subsections.delivery_notes',
    descriptionKey: 'facturation_sections.bons_livraison.description',
    allowedTypeKeys: ['delivery-note', 'delivery_note', 'delivery'],
    createActions: [
      { type: 'delivery-note', labelKey: 'facturation_sections.create.delivery_note' },
    ],
  },
  'avoirs-vente-achats': {
    route: '/facturation/avoirs-vente-achats',
    titleKey: 'modules.facturation.subsections.credit_notes',
    descriptionKey: 'facturation_sections.avoirs_vente_achats.description',
    allowedTypeKeys: ['credit-note', 'credit_note', 'debit-note', 'debit_note'],
    documentFilter: (doc) => {
      const kn = canonicalArchiveDocTypeKey(doc?.type);
      if (kn === 'debit_note') return true;
      return isClientCreditNoteDocument(doc) || isSupplierCreditNoteDocument(doc);
    },
    createActions: [
      { type: 'credit-note', party: 'client', labelKey: 'facturation_sections.create.credit_note_client' },
      { type: 'debit-note', labelKey: 'facturation_sections.create.debit_note_client' },
      { type: 'credit-note', party: 'supplier', labelKey: 'facturation_sections.create.credit_note_supplier' },
    ],
  },
  'justificatifs-reglement': {
    route: '/facturation/justificatifs-reglement',
    titleKey: 'modules.facturation.subsections.settlement_proofs',
    descriptionKey: 'facturation_sections.justificatifs_reglement.description',
    allowedTypeKeys: ['payment-voucher', 'payment_voucher', 'payment-slip', 'payment_slip'],
    createActions: [
      { type: 'payment-voucher', labelKey: 'facturation_sections.create.payment_voucher' },
    ],
  },
};

/** Lookup section config by pathname (e.g. `/facturation/demande-prix`). */
export function getFacturationSectionArchiveByRoute(pathname) {
  const normalized = String(pathname ?? '').replace(/\/+$/, '');
  return Object.values(FACTURATION_SECTION_ARCHIVES).find((cfg) => cfg.route === normalized) ?? null;
}

/** Filter documents for a section archive page. */
export function filterDocumentsForSection(documents, sectionConfig) {
  if (!sectionConfig || !Array.isArray(documents)) return documents ?? [];
  return documents.filter((doc) => {
    if (!matchesAllowedTypes(doc, sectionConfig.allowedTypeKeys)) return false;
    if (typeof sectionConfig.documentFilter === 'function' && !sectionConfig.documentFilter(doc)) {
      return false;
    }
    return true;
  });
}

/** Build document-creation URL with type, optional party, and return path. */
export function buildSectionCreateDocumentUrl(sectionConfig, action) {
  const params = new URLSearchParams();
  params.set('type', action.type);
  if (action.party) params.set('party', action.party);
  if (sectionConfig?.route) params.set('from', sectionConfig.route.replace(/^\//, ''));
  return `/document-creation?${params.toString()}`;
}

/** Full document picker (sales / purchases tabs) — same as legacy « إنشاء مستند ». */
export function buildFullDocumentCreationUrl(sectionConfig) {
  const params = new URLSearchParams();
  if (sectionConfig?.route) params.set('from', sectionConfig.route.replace(/^\//, ''));
  const qs = params.toString();
  return qs ? `/document-creation?${qs}` : '/document-creation';
}

export const FACTURATION_SECTION_ARCHIVE_ROUTES = Object.values(FACTURATION_SECTION_ARCHIVES).map(
  (cfg) => cfg.route,
);

/** Resolve section return route from DocumentCreation `from` query (e.g. `facturation/factures`). */
export function resolveSectionRouteFromFromParam(fromParam) {
  if (!fromParam || fromParam === 'inventaire' || fromParam === 'logistique') return null;
  const route = String(fromParam).startsWith('/') ? fromParam : `/${fromParam}`;
  return getFacturationSectionArchiveByRoute(route)?.route ?? null;
}
